
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { UseFormReturn } from "react-hook-form";
import { motion } from "framer-motion";
import { FormValues } from "@/types/loan";
import { HebrewLoanDetailsSection } from "./HebrewLoanDetailsSection";
import { HebrewPersonalInfoSection } from "./HebrewPersonalInfoSection";
import { HebrewLoanInfoNotices } from "./HebrewLoanInfoNotices";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { LoaderCircle } from "lucide-react";

interface HebrewApplicationFormProps {
  form: UseFormReturn<FormValues>;
  handleSubmit: (data: FormValues) => Promise<void>;
  loanAmount: number;
  setLoanAmount: (value: number) => void;
  loanTerm: number;
  setLoanTerm: (value: number) => void;
  interest: number;
  isSubmitting: boolean;
  monthlyPayment?: number;
  totalPaid?: number;
  interestAndFees?: number;
  monthlyFee?: number;
  setupFee?: number;
  setTermsOpen?: (value: boolean) => void;
  setPrivacyPolicyOpen?: (value: boolean) => void;
  setLoanType?: (value: string) => void;
}

export const HebrewLoanApplicationForm = ({
  form,
  handleSubmit,
  loanAmount,
  setLoanAmount,
  loanTerm,
  setLoanTerm,
  interest,
  isSubmitting,
  monthlyPayment = 0,
  totalPaid = 0,
  interestAndFees = 0,
  monthlyFee = 0,
  setupFee = 0,
  setTermsOpen = () => {},
  setPrivacyPolicyOpen = () => {},
  setLoanType = () => {}
}: HebrewApplicationFormProps) => {
  const isMobile = useIsMobile();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="mb-10 w-full"
    >
      <Card className="bg-white mb-8 overflow-hidden w-full max-w-full shadow-md">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 md:p-6">
          <CardTitle className="text-xl md:text-2xl">הגש בקשה להלוואה עכשיו</CardTitle>
          <CardDescription className="text-blue-100 text-sm md:text-base">מלא את הטופס וקבל הצעות במהירות</CardDescription>
        </CardHeader>
        <CardContent className="p-2 md:p-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 w-full">
              <div className="space-y-6 w-full">
                <HebrewLoanDetailsSection 
                  form={form}
                  loanAmount={loanAmount}
                  setLoanAmount={setLoanAmount}
                  loanTerm={loanTerm}
                  setLoanTerm={setLoanTerm}
                  monthlyPayment={monthlyPayment}
                  totalPaid={totalPaid}
                  interestAndFees={interestAndFees}
                  interest={interest}
                  monthlyFee={monthlyFee}
                  setupFee={setupFee}
                  setLoanType={setLoanType}
                />
                
                <HebrewPersonalInfoSection 
                  form={form}
                  setTermsOpen={setTermsOpen}
                  setPrivacyPolicyOpen={setPrivacyPolicyOpen}
                />
              </div>
              
              <HebrewLoanInfoNotices isSubmitting={isSubmitting} />
              
              {!isMobile && (
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 mt-4 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-md rounded-lg font-semibold text-lg h-14"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                      שולח...
                    </>
                  ) : (
                    "השווה הצעות הלוואה"
                  )}
                </Button>
              )}
            </form>
          </Form>
        </CardContent>
      </Card>
    </motion.div>
  );
};
